import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    clearQuestionnaireAnswers,
    loadQuestionnaires,
    setCurrentQuestionnaire
} from '../../store/participantService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../utils/formValidation/commonConstants';
import { stableSort } from '../../utils/sortFunctions';
import { hasRole, MANAGER, QUALITY, SUPERUSER } from '../../utils/userRoles';
import Button from '../formElements/Button';
import DynamicSearch from '../search/DynamicSearch';
import ResultsTable from '../table/ResultsTable';

import QuestionnaireRow from './QuestionnaireRow';

import app from '../../app.module.css';
import classes from './questionnaireStyles/questionnaireToolbar.module.css';

const headCells = [
    { id: 'id', numeric: false, label: 'ID' },
    { id: 'title', numeric: false, label: 'Title' },
    { id: 'dateCreated', numeric: false, label: 'Date Created' },
    { id: 'inactive', numeric: false, label: 'Active' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'dateCreated',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [MANAGER, QUALITY, SUPERUSER];

const Questionnaire = () => {
    //HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState(rows);
    const [searchTerm, setSearchTerm] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const questionnaires = useSelector((state) => state.entities.participantService.questionnaires);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadQuestionnaires());
    }, []);

    useEffect(() => {
        setRows(questionnaires);
        setFilteredRows(questionnaires);
    }, [questionnaires]);

    useEffect(() => {
        const filteredRows = !searchTerm
            ? rows
            : rows.filter((el) => el.title.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredRows(filteredRows);
    }, [searchTerm]);

    // HELPER FNS
    const createRows = () => {
        return stableSort(filteredRows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <QuestionnaireRow key={el.id} row={el} />
        ));
    };

    // EVENT HANDLERS
    const onSearch = (searchRes) => setSearchTerm(searchRes);

    const onCreateQuestionnaire = () => {
        dispatch(setCurrentQuestionnaire({}));
        dispatch(clearQuestionnaireAnswers());
        navigate('/build_questionnaire');
    };

    // RENDER
    return (
        <div className={app.pageContainer}>
            <h1 className={app.mainHeading}>Manage Questionnaire</h1>
            <div className={classes.questionnaireToolBar}>
                <DynamicSearch
                    search={onSearch}
                    searchTerm={searchTerm}
                    placeholder="Enter search term"
                />
            </div>
            {filteredRows.length < 1 ? (
                <div>No questionnaires found</div>
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}
            <Button
                id="CreateQuestionnaireButton"
                disabled={!hasRole(acceptedRoles, roles)}
                type="button"
                content="Create Questionnaire"
                icon={true}
                onClick={onCreateQuestionnaire}
            />
        </div>
    );
};

export default Questionnaire;
